import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  DollarSign, 
  FileText, 
  Users, 
  Settings, 
  Menu, 
  X, 
  LogOut,
  Moon,
  Sun,
  ChevronLeft,
  Link2,
  TrendingUp,
  UserPlus,
  ShoppingCart,
  ClipboardList,
  BarChart3,
  Hotel,
  Megaphone,
  CalendarCheck
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const staffNavItems = [
  { path: '/dashboard', label: 'Performans', icon: TrendingUp },
  { path: '/commissions', label: 'Prim', icon: DollarSign },
  { path: '/my-sales', label: 'Satışlarım', icon: ShoppingCart },
  { path: '/second-visit', label: '2. Visit', icon: CalendarCheck },
  { path: '/price-lists', label: 'Fiyat Listesi', icon: FileText },
  { path: '/hotel-reservation', label: 'Otel Rezervasyon', icon: Hotel },
  { path: '/duyurular', label: 'Duyurular', icon: Megaphone },
];

const adminNavItems = [
  { path: '/dashboard', label: 'Performans', icon: TrendingUp },
  { path: '/commissions', label: 'Prim', icon: DollarSign },
  { path: '/my-sales', label: 'Satışlar', icon: ShoppingCart },
  { path: '/second-visit', label: '2. Visit', icon: CalendarCheck },
  { path: '/price-lists', label: 'Fiyat Listesi', icon: FileText },
  { path: '/admin/sales-tracking', label: 'Satış Takip', icon: ClipboardList },
  { path: '/admin/monthly-data-sales', label: 'Aylık Data Satış', icon: BarChart3 },
  { path: '/admin/users', label: 'Kullanıcı Yönetimi', icon: Users },
  { path: '/admin/leads', label: 'Lead Girişi', icon: UserPlus },
  { path: '/duyurular', label: 'Duyurular', icon: Megaphone },
  { path: '/admin/integrations', label: 'Entegrasyonlar', icon: Link2 },
  { path: '/admin/settings', label: 'Ayarlar', icon: Settings },
];

export const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // localStorage'dan sidebar durumunu oku
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? saved === 'true' : true;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, getAuthHeader } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const navItems = isAdmin() ? adminNavItems : staffNavItems;

  // Sidebar durumunu localStorage'a kaydet
  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    localStorage.setItem('sidebarOpen', String(newState));
  };

  // Fetch unseen announcement count
  const fetchUnseenCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/announcements/unseen/count`, getAuthHeader());
      setUnseenCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch unseen count:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchUnseenCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnseenCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnseenCount]);

  // Refresh count when navigating to announcements page
  useEffect(() => {
    if (location.pathname === '/duyurular') {
      // Small delay to allow marking as seen
      setTimeout(fetchUnseenCount, 1000);
    }
  }, [location.pathname, fetchUnseenCount]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const getPageTitle = () => {
    const item = navItems.find(item => item.path === location.pathname);
    return item?.label || 'Panel';
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-effect h-16 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="font-bold text-lg font-[Manrope]">Dentordu</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-mobile">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-50"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-[280px] bg-sidebar-background border-r z-50 p-4"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-bold text-xl font-[Manrope]">Dentordu</span>
                <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <nav className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const showBadge = item.path === '/duyurular' && unseenCount > 0;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive 
                          ? 'bg-primary text-primary-foreground' 
                          : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium flex-1">{item.label}</span>
                      {showBadge && (
                        <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse">
                          {unseenCount > 9 ? '9+' : unseenCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex fixed left-0 top-0 bottom-0 flex-col border-r bg-sidebar-background transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-[260px]' : 'w-[72px]'
        }`}
        data-testid="sidebar"
      >
        <div className={`h-16 flex items-center border-b px-4 ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
          {sidebarOpen && <span className="font-bold text-xl font-[Manrope]">Dentordu</span>}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="btn-scale"
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={`h-5 w-5 transition-transform duration-300 ${!sidebarOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const showBadge = item.path === '/duyurular' && unseenCount > 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors relative ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                } ${!sidebarOpen ? 'justify-center' : ''}`}
                title={!sidebarOpen ? item.label : undefined}
              >
                <div className="relative">
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {/* Badge for collapsed sidebar */}
                  {!sidebarOpen && showBadge && (
                    <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                      {unseenCount > 9 ? '9+' : unseenCount}
                    </span>
                  )}
                </div>
                {sidebarOpen && (
                  <>
                    <span className="font-medium flex-1">{item.label}</span>
                    {showBadge && (
                      <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse">
                        {unseenCount > 9 ? '9+' : unseenCount}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        <div className={`p-3 border-t ${!sidebarOpen ? 'flex justify-center' : ''}`}>
          {sidebarOpen ? (
            <div className="flex items-center gap-3 p-2">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.role === 'admin' ? 'Yönetici' : 'Personel'}
                </p>
              </div>
            </div>
          ) : (
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-[260px]' : 'lg:ml-[72px]'}`}>
        {/* Desktop Header */}
        <header className="hidden lg:flex sticky top-0 z-30 glass-effect h-16 px-6 items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold font-[Manrope]">{getPageTitle()}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  {sidebarOpen && <span className="text-sm font-medium">{user?.name}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem className="text-muted-foreground" disabled>
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 pt-20 lg:pt-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
